class SponsorshipTshirtAddon extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.getAttribute('data-section-id');
    this.tshirtProductId = this.getAttribute('data-tshirt-product-id');
    this.checkbox = this.querySelector('.sponsorship-tshirt-checkbox');
    this.sizeWrapper = this.querySelector(`#tshirt-size-wrapper-${this.sectionId}`);
    this.sizeSelect = this.querySelector('.sponsorship-tshirt-size');
    this.variantsJson = this.querySelector(`#TShirtVariants-${this.sectionId}`);
    this.variants = [];
    this.cartAddInProgress = false;
  }

  connectedCallback() {
    // Toggle size dropdown on checkbox change
    this.checkbox?.addEventListener('change', (e) => {
      this.toggleSizeDropdown(e.target.checked);
    });

    // Fetch T-shirt product variants
    this.fetchTshirtVariants();

    // Subscribe to cart update events
    const unsubscribe = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      this.onSponsorshipAdded.bind(this)
    );
    this.unsubscribe = unsubscribe;

    // Add form validation for cat name
    this.setupFormValidation();
  }

  setupFormValidation() {
    // sponsorship-tshirt-addon lives in the quantity_selector block; the <form>
    // and <product-form> are rendered separately in the buy_buttons block.
    // They are siblings in the DOM, so closest() never finds them.
    // Look them up by the section-scoped IDs instead.
    const form = document.getElementById(`product-form-${this.sectionId}`);
    if (!form) return;

    // Use capture phase so this fires before product-form.js's own submit handler.
    // stopImmediatePropagation prevents product-form.js from running at all when invalid.
    form.addEventListener(
      'submit',
      (e) => {
        if (!this.validateCatName()) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      },
      { capture: true }
    );

    // Dynamic checkout buttons (Shop Pay, Buy Now, etc.) render inside iframes
    // and completely bypass the form — JS cannot intercept their clicks directly.
    // Solution: place a transparent overlay div on top that blocks pointer events
    // and shows the error when cat-name is empty.
    this.setupPaymentButtonOverlay();

    // Keep overlay in sync as the user types
    document.getElementById('cat-name')?.addEventListener('input', () => {
      this.updatePaymentButtonOverlay();
    });
  }

  validateCatName() {
    const catNameInput = document.getElementById('cat-name');
    if (!catNameInput?.value.trim()) {
      this.showCatNameError("Please enter your cat's name");
      catNameInput?.focus();
      return false;
    }
    return true;
  }

  setupPaymentButtonOverlay() {
    // The payment buttons live inside the <product-form> custom element,
    // which is a sibling in the DOM — look it up by section ID.
    const productFormEl = document.querySelector(`product-form[data-section-id="${this.sectionId}"]`);
    if (!productFormEl) return;

    // .shopify-payment-button is the container rendered by {{ form | payment_button }}
    const paymentButtonContainer = productFormEl.querySelector('.shopify-payment-button');
    if (!paymentButtonContainer) return;

    // Wrap in a relative-position div so the absolute overlay is scoped to it
    const wrapper = document.createElement('div');
    wrapper.style.position = 'relative';
    paymentButtonContainer.parentNode.insertBefore(wrapper, paymentButtonContainer);
    wrapper.appendChild(paymentButtonContainer);

    const overlay = document.createElement('div');
    overlay.className = 'cat-name-payment-overlay';
    overlay.style.cssText = 'position:absolute;inset:0;cursor:pointer;z-index:1;';
    overlay.addEventListener('click', () => {
      this.validateCatName();
    });
    wrapper.appendChild(overlay);

    this.paymentButtonOverlay = overlay;
    this.updatePaymentButtonOverlay();
  }

  updatePaymentButtonOverlay() {
    if (!this.paymentButtonOverlay) return;
    const catNameInput = document.getElementById('cat-name');
    const isEmpty = !catNameInput?.value.trim();
    this.paymentButtonOverlay.style.display = isEmpty ? 'block' : 'none';
  }

  showCatNameError(message) {
    // Render the error directly below the cat-name input field
    let errorEl = document.getElementById('cat-name-error');
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.id = 'cat-name-error';
      errorEl.setAttribute('role', 'alert');
      errorEl.style.cssText = 'color:#d32f2f;font-size:0.875rem;margin-top:0.25rem;';
      document.getElementById('cat-name')?.insertAdjacentElement('afterend', errorEl);
    }

    errorEl.textContent = message;
    errorEl.style.display = 'block';

    setTimeout(() => {
      errorEl.style.display = 'none';
    }, 5000);
  }

  fetchTshirtVariants() {
    // Fetch the T-shirt product by handle to get its variants
    // Using handle instead of ID because Shopify's public API works better with handles
    fetch(`/products/sponsor-tee-2026.js`)
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then(product => {
        this.variants = product.variants || [];
        console.log('T-shirt variants loaded:', this.variants.length, 'variants');
        // Populate size dropdown after variants are loaded
        this.populateSizeOptions();
      })
      .catch(err => {
        console.error('Failed to load T-shirt product:', err);
      });
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  toggleSizeDropdown(isChecked) {
    if (isChecked) {
      this.sizeWrapper.style.display = 'block';
      this.sizeSelect.disabled = false;
    } else {
      this.sizeWrapper.style.display = 'none';
      this.sizeSelect.disabled = true;
      this.sizeSelect.value = ''; // Clear selection when unchecked
    }
  }

  populateSizeOptions() {
    // Extract sizes from variants (Color + Size structure)
    // Variants have option1=Color, option2=Size
    // We want to show only unique sizes in the dropdown
    if (!this.variants || this.variants.length === 0) {
      console.warn('No T-shirt variants available');
      return;
    }

    // Clear any existing options except the default
    while (this.sizeSelect.options.length > 1) {
      this.sizeSelect.removeChild(this.sizeSelect.lastChild);
    }

    const sizes = new Set();
    this.variants.forEach(variant => {
      // Size is in option2 (option1 is color)
      if (variant.option2) sizes.add(variant.option2);
    });

    if (sizes.size === 0) {
      console.warn('No sizes found in T-shirt variants');
      return;
    }

    // Sort sizes in order: XS, S, M, L, XL, 2XL, 3XL if applicable
    const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];
    const sortedSizes = Array.from(sizes).sort((a, b) => {
      const aIndex = sizeOrder.indexOf(a);
      const bIndex = sizeOrder.indexOf(b);
      if (aIndex === -1) return 1; // Unknown sizes go to end
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });

    sortedSizes.forEach(size => {
      const option = document.createElement('option');
      option.value = size;
      option.textContent = size;
      this.sizeSelect.appendChild(option);
    });

    console.log('T-shirt sizes populated:', Array.from(sortedSizes));
  }

  onSponsorshipAdded(data) {
    // Only process if checkbox is checked
    if (!this.checkbox?.checked) {
      return;
    }

    // Validate size selection
    if (!this.sizeSelect.value) {
      this.showError('Please select a T-shirt size before adding to cart');
      return;
    }

    // Find the selected T-shirt variant by matching size
    // Since we have Color + Size, find the first variant with the selected size
    const selectedSize = this.sizeSelect.value;
    const tshirtVariant = this.variants.find(
      v => v.option2 === selectedSize
    );

    if (!tshirtVariant) {
      this.showError('Selected T-shirt size is not available');
      return;
    }

    // Prevent multiple concurrent add-to-cart requests
    if (this.cartAddInProgress) {
      return;
    }

    // Add T-shirt to cart
    this.addTshirtToCart(tshirtVariant);
  }

  addTshirtToCart(variant) {
    this.cartAddInProgress = true;

    const formData = new FormData();
    formData.append('id', variant.id);
    formData.append('quantity', 1);

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
    })
      .then(response => {
        if (!response.ok) throw new Error('Failed to add T-shirt to cart');
        return response.json();
      })
      .then(cart => {
        this.cartAddInProgress = false;
        this.showSuccess(`T-shirt (${variant.option1} - ${variant.option2}) added to cart!`);
      })
      .catch(err => {
        this.cartAddInProgress = false;
        this.showError(`Failed to add T-shirt: ${err.message}`);
      });
  }

  showError(message) {
    // Create or update error message element
    let errorEl = this.querySelector('.sponsorship-tshirt-error');
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.className = 'sponsorship-tshirt-error';
      this.insertBefore(errorEl, this.checkbox.parentElement.nextElementSibling);
    }

    errorEl.textContent = message;
    errorEl.style.display = 'block';
    errorEl.style.color = '#d32f2f';
    errorEl.style.fontSize = '0.875rem';
    errorEl.style.marginTop = '0.5rem';

    // Auto-hide error after 5 seconds
    setTimeout(() => {
      if (errorEl) {
        errorEl.style.display = 'none';
      }
    }, 5000);
  }

  showSuccess(message) {
    // Log to console for debugging
    console.log('Sponsorship T-Shirt:', message);
    // The cart notification from product-form.js will handle the success UI
  }
}

customElements.define('sponsorship-tshirt-addon', SponsorshipTshirtAddon);
